import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { Save, ArrowLeft, Upload, Eye } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const BlogEditor = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    date: new Date().toISOString().split('T')[0],
    readTime: 5,
    image: '',
    excerpt: '',
    content: '',
    tags: []
  });

  const [tagInput, setTagInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (isEdit) {
      fetchPost();
    }
  }, [id]);

  const fetchPost = async () => {
    try {
      // First get all posts, then find by ID
      const response = await fetch(`/api/posts.php`);
      const posts = await response.json();
      const post = posts.find(p => p.id === parseInt(id));
      
      if (post) {
        setFormData({
          id: post.id,
          title: post.title,
          slug: post.slug,
          date: post.date,
          readTime: post.readTime || post.read_time,
          image: post.image,
          excerpt: post.excerpt,
          content: post.content,
          tags: post.tags || []
        });
      } else {
        alert('Post not found');
        navigate('/admin/dashboard');
      }
    } catch (err) {
      alert('Failed to load post');
      navigate('/admin/dashboard');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Auto-generate slug from title
    if (name === 'title' && !isEdit) {
      const slug = value.toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      setFormData(prev => ({ ...prev, slug }));
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('image', file);

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/upload.php', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataUpload
      });

      const data = await response.json();
      if (response.ok) {
        setFormData(prev => ({ ...prev, image: data.url }));
      } else {
        alert(data.error || 'Upload failed');
      }
    } catch (err) {
      alert('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const token = localStorage.getItem('admin_token');
    const url = isEdit ? '/api/posts.php' : '/api/posts.php';
    const method = isEdit ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(isEdit ? { ...formData, id } : formData)
      });

      const data = await response.json();
      if (response.ok) {
        navigate('/admin/dashboard');
      } else {
        alert(data.error || 'Failed to save post');
      }
    } catch (err) {
      alert('Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container mx-auto px-4 pt-32 pb-16">
      <div className="max-w-5xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className={`text-4xl font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {isEdit ? 'Edit Post' : 'Create New Post'}
          </h1>
          <div className="flex gap-4">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                isDarkMode
                  ? 'bg-gray-700/50 text-gray-200 hover:bg-gray-700'
                  : 'bg-gray-200/50 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Eye className="w-5 h-5" />
              {showPreview ? 'Edit' : 'Preview'}
            </button>
            <button
              onClick={() => navigate('/admin/dashboard')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                isDarkMode
                  ? 'bg-gray-700/50 text-gray-200 hover:bg-gray-700'
                  : 'bg-gray-200/50 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <ArrowLeft className="w-5 h-5" />
              Back
            </button>
          </div>
        </div>

        {showPreview ? (
          <div className="min-h-screen">
            <article className={`max-w-4xl mx-auto px-4 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
              {/* Header Section */}
              <header className="mb-8">
                <div className="flex flex-wrap gap-2 mb-4">
                  {(formData.tags || []).map((tag) => (
                    <span
                      key={tag}
                      className={`px-3 py-1 text-xs font-medium rounded-full backdrop-blur-sm ${
                        isDarkMode
                          ? 'bg-gray-700/30 text-gray-200 border border-gray-600/30'
                          : 'bg-gray-100/30 text-gray-800 border border-gray-200/30'
                      }`}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <h1 className={`text-4xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {formData.title || 'Untitled'}
                </h1>
                <div className={`text-sm mb-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {formData.date} · {formData.readTime} min read
                </div>
              </header>

              {/* Featured Image */}
              {formData.image && (
                <div className="mb-12">
                  <div className={`rounded-2xl overflow-hidden backdrop-blur-md shadow-lg border ${
                    isDarkMode
                      ? 'bg-gray-800/30 border-gray-700/30'
                      : 'bg-white/30 border-gray-200/30'
                  }`}>
                    <img
                      src={formData.image}
                      alt={formData.title}
                      className="w-full h-[400px] object-cover object-center"
                    />
                  </div>
                </div>
              )}

              {/* Article Content */}
              <div className={`rounded-2xl backdrop-blur-md p-8 shadow-lg border ${
                isDarkMode
                  ? 'bg-gray-800/30 border-gray-700/30'
                  : 'bg-white/30 border-gray-200/30'
              }`}>
                <div className={`font-poppins ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                  <ReactMarkdown
                    className="font-poppins"
                    components={{
                      h1: ({ children }) => (
                        <h1 className="text-4xl font-semibold mb-6 font-poppins">
                          {children}
                        </h1>
                      ),
                      h2: ({ children }) => (
                        <h2 className="text-3xl font-semibold mb-5 font-poppins">
                          {children}
                        </h2>
                      ),
                      h3: ({ children }) => (
                        <h3 className="text-2xl font-semibold mb-4 font-poppins">
                          {children}
                        </h3>
                      ),
                      p: ({ children }) => (
                        <p className="text-[0.9em] leading-relaxed mb-4 font-poppins">
                          {children}
                        </p>
                      ),
                      ul: ({ children }) => (
                        <ul className="list-disc pl-6 mb-4 space-y-2 font-poppins">
                          {children}
                        </ul>
                      ),
                      ol: ({ children }) => (
                        <ol className="list-decimal pl-6 mb-4 space-y-2 font-poppins">
                          {children}
                        </ol>
                      ),
                      li: ({ children }) => (
                        <li className="text-[0.9em] font-poppins">{children}</li>
                      ),
                    }}
                  >
                    {formData.content}
                  </ReactMarkdown>
                </div>
              </div>
            </article>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className={`p-6 rounded-2xl backdrop-blur-md shadow-lg border ${
              isDarkMode
                ? 'bg-gray-800/30 border-gray-700/30'
                : 'bg-white/30 border-gray-200/30'
            }`}>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? 'text-gray-200' : 'text-gray-700'
                  }`}>
                    Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg backdrop-blur-sm border ${
                      isDarkMode
                        ? 'bg-gray-700/50 border-gray-600/50 text-white'
                        : 'bg-white/50 border-gray-300/50 text-gray-900'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500/50`}
                    required
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? 'text-gray-200' : 'text-gray-700'
                  }`}>
                    Slug *
                  </label>
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg backdrop-blur-sm border ${
                      isDarkMode
                        ? 'bg-gray-700/50 border-gray-600/50 text-white'
                        : 'bg-white/50 border-gray-300/50 text-gray-900'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500/50`}
                    required
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? 'text-gray-200' : 'text-gray-700'
                  }`}>
                    Date
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg backdrop-blur-sm border ${
                      isDarkMode
                        ? 'bg-gray-700/50 border-gray-600/50 text-white'
                        : 'bg-white/50 border-gray-300/50 text-gray-900'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500/50`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? 'text-gray-200' : 'text-gray-700'
                  }`}>
                    Read Time (minutes)
                  </label>
                  <input
                    type="number"
                    name="readTime"
                    value={formData.readTime}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg backdrop-blur-sm border ${
                      isDarkMode
                        ? 'bg-gray-700/50 border-gray-600/50 text-white'
                        : 'bg-white/50 border-gray-300/50 text-gray-900'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500/50`}
                    min="1"
                  />
                </div>
              </div>

              <div className="mt-6">
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Featured Image
                </label>
                <div className="flex gap-4 items-center">
                  <input
                    type="text"
                    name="image"
                    value={formData.image}
                    onChange={handleChange}
                    placeholder="/images/your-image.jpg"
                    className={`flex-1 px-4 py-3 rounded-lg backdrop-blur-sm border ${
                      isDarkMode
                        ? 'bg-gray-700/50 border-gray-600/50 text-white'
                        : 'bg-white/50 border-gray-300/50 text-gray-900'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500/50`}
                  />
                  <label className={`flex items-center gap-2 px-4 py-3 rounded-lg font-medium cursor-pointer transition-all duration-200 ${
                    isDarkMode
                      ? 'bg-gray-700/50 text-gray-200 hover:bg-gray-700'
                      : 'bg-gray-200/50 text-gray-700 hover:bg-gray-200'
                  }`}>
                    <Upload className="w-5 h-5" />
                    {uploading ? 'Uploading...' : 'Upload'}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={uploading}
                    />
                  </label>
                </div>
                {formData.image && (
                  <img
                    src={formData.image}
                    alt="Preview"
                    className="mt-4 w-full h-48 object-cover rounded-lg"
                  />
                )}
              </div>

              <div className="mt-6">
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Tags
                </label>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                    placeholder="Add a tag"
                    className={`flex-1 px-4 py-3 rounded-lg backdrop-blur-sm border ${
                      isDarkMode
                        ? 'bg-gray-700/50 border-gray-600/50 text-white'
                        : 'bg-white/50 border-gray-300/50 text-gray-900'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500/50`}
                  />
                  <button
                    type="button"
                    onClick={handleAddTag}
                    className={`px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                      isDarkMode
                        ? 'bg-gray-700/50 text-gray-200 hover:bg-gray-700'
                        : 'bg-gray-200/50 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map((tag) => (
                    <span
                      key={tag}
                      className={`px-3 py-1 text-sm font-medium rounded-full backdrop-blur-sm cursor-pointer ${
                        isDarkMode
                          ? 'bg-gray-700/30 text-gray-200 border border-gray-600/30 hover:bg-red-500/20'
                          : 'bg-gray-100/30 text-gray-800 border border-gray-200/30 hover:bg-red-100/50'
                      }`}
                      onClick={() => handleRemoveTag(tag)}
                    >
                      {tag} ×
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-6">
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Excerpt *
                </label>
                <textarea
                  name="excerpt"
                  value={formData.excerpt}
                  onChange={handleChange}
                  rows="3"
                  className={`w-full px-4 py-3 rounded-lg backdrop-blur-sm border ${
                    isDarkMode
                      ? 'bg-gray-700/50 border-gray-600/50 text-white'
                      : 'bg-white/50 border-gray-300/50 text-gray-900'
                  } focus:outline-none focus:ring-2 focus:ring-blue-500/50`}
                  required
                />
              </div>

              <div className="mt-6">
                <label className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Content (Markdown) *
                </label>
                <textarea
                  name="content"
                  value={formData.content}
                  onChange={handleChange}
                  rows="20"
                  className={`w-full px-4 py-3 rounded-lg backdrop-blur-sm border font-mono text-sm ${
                    isDarkMode
                      ? 'bg-gray-700/50 border-gray-600/50 text-white'
                      : 'bg-white/50 border-gray-300/50 text-gray-900'
                  } focus:outline-none focus:ring-2 focus:ring-blue-500/50`}
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={saving}
              className={`w-full flex items-center justify-center gap-2 py-4 px-6 rounded-lg font-medium transition-all duration-200 ${
                isDarkMode
                  ? 'bg-white/90 text-black hover:bg-white disabled:bg-gray-600'
                  : 'bg-gray-900/90 text-white hover:bg-gray-900 disabled:bg-gray-400'
              } disabled:cursor-not-allowed`}
            >
              <Save className="w-5 h-5" />
              {saving ? 'Saving...' : (isEdit ? 'Update Post' : 'Create Post')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default BlogEditor;
